import { useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { ProductDetails } from '@/components/ProductCard/components/ProductDetails'
import { ProductDocuments } from '@/components/ProductCard/components/ProductDocuments'
import { ProductCardBreakdownContent, ProductCardTotalPriceContent } from '@/components/ProductCard/ProductCard'
import { useOfferDetails } from '@/components/ProductCard/useOfferDetails'
import { OFFER_TO_EDIT_QUERY_PARAM } from '@/components/ProductPage/useOfferToEdit'
import { BundleDiscountProgress } from '@/features/bundleDiscount/components/BundleDiscountProgress/BundleDiscountProgress'
import { DiscountInfoCard } from '@/features/CrossSell/components/DiscountInfoCard'
import { useDiscountNotification } from '@/features/CrossSell/hooks/useDiscountNotification'
import { type OfferRecommendation } from '@/features/CrossSell/hooks/useRecommendations'
import {
  appendPriceIntentAttributionQuery,
  EXTERNAL_INSURANCE_OFFER_RECOMMENDATION_USER_FLOW,
} from '@/features/recommendations/priceIntentAttributionStorage'
import { Button } from 'ui/src/components/Button/Button'
import { BaseDialog } from 'ui/src/components/Dialog/BaseDialog'
import { Divider } from 'ui/src/components/Divider/Divider'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Tabs } from 'ui/src/components/Tabs/Tabs'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  offer: OfferRecommendation['offer']
  eligibleProductCount: number
  priceCalculatorPageLink: string
}

export function RecommendedOfferDialog({
  open,
  onOpenChange,
  offer,
  eligibleProductCount,
  priceCalculatorPageLink,
}: Props) {
  const { t } = useTranslation(['cart', 'common', 'purchase-form'])
  const offerDetails = useOfferDetails(offer)
  const router = useRouter()
  const [isNavigatingToEdit, startEditTransition] = useTransition()

  const priceMatch = offer.priceMatch
  const discountNotification = useDiscountNotification(offer)

  if (!priceMatch) return null

  const handleEdit = () => {
    const targetUrl = new URL(priceCalculatorPageLink, window.location.origin)
    targetUrl.searchParams.set(OFFER_TO_EDIT_QUERY_PARAM, offer.id)
    startEditTransition(() =>
      router.push(
        appendPriceIntentAttributionQuery(targetUrl.toString(), {
          userFlow: EXTERNAL_INSURANCE_OFFER_RECOMMENDATION_USER_FLOW,
        }),
      ),
    )
  }

  return (
    <BaseDialog.Root open={open} onOpenChange={onOpenChange}>
      <BaseDialog.Overlay frosted>
        <BaseDialog.Content
          closeOnPointerDownOutside
          className={yStack({ gap: 'md', padding: 'lg' })}
          maxWidth="31rem"
          mobileLayout="sheet"
        >
          <BaseDialog.Header>
            <BaseDialog.Title>{t('RECOMMENDED_OFFER_DIALOG_YOUR_OFFER')}</BaseDialog.Title>
            <BaseDialog.CloseButton aria-label={t('common:DIALOG_CLOSE')} />
          </BaseDialog.Header>

          <BaseDialog.Body>
            <Tabs.Root defaultValue="price-details" fullWidth={true} style={{ padding: 0 }}>
              <div className={yStack({ gap: 'md' })}>
                <Tabs.List type="filled" layout="evenly-spaced">
                  <Tabs.Trigger value="price-details">{t('RECOMMENDED_OFFER_DIALOG_PRICE_TAB')}</Tabs.Trigger>
                  <Tabs.Trigger value="your-information">{t('RECOMMENDED_OFFER_DIALOG_INFO_TAB')}</Tabs.Trigger>
                </Tabs.List>

                <div>
                  <Tabs.Content value="price-details" className={yStack({ gap: 'md' })}>
                    <div className={yStack({ gap: 'md' })}>
                      <ProductCardBreakdownContent offer={offer} />

                      {discountNotification && <DiscountInfoCard {...discountNotification} />}

                      <ProductCardTotalPriceContent offer={offer} />
                    </div>

                    {eligibleProductCount > 0 && (
                      <>
                        <Divider />

                        <div className={yStack({ gap: 'md' })}>
                          <Heading as="h3" variant="standard.18">
                            {t('RECOMMENDED_OFFER_DIALOG_BUNDLE_DISCOUNT_HEADING')}
                          </Heading>

                          <BundleDiscountProgress eligibleInsuranceCount={eligibleProductCount} />
                        </div>
                      </>
                    )}
                  </Tabs.Content>

                  <Tabs.Content value="your-information">
                    <div className={yStack({ gap: 'lg', paddingInline: 'xxs' })}>
                      <ProductDetails details={offerDetails} />
                      <ProductDocuments documents={offer.variant.documents} />
                    </div>
                  </Tabs.Content>
                </div>
              </div>
            </Tabs.Root>
          </BaseDialog.Body>

          <BaseDialog.Footer className={sprinkles({ marginTop: 'lg' })}>
            <Button fullWidth loading={isNavigatingToEdit} onClick={handleEdit} size="large">
              {t('RECOMMENDED_OFFER_EDIT_BUTTON')}
            </Button>
          </BaseDialog.Footer>
        </BaseDialog.Content>
      </BaseDialog.Overlay>
    </BaseDialog.Root>
  )
}
